import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';

import { AutoScalingButtonSet } from '../../components/Buttons/AutoScalingButtonSet';
import { Container, Body, RockTheVoteLabel } from '../DefaultStyles/Details';
import {
    Content,
    Description,
    Details,
    DetailsContainer,
    Footer,
    RTVLogo,
    Title
} from '../DefaultStyles/Shared';
import { isLongText } from '../../components/Shared/commonFunctions';
import AddressPopup from '../../components/FindPollingStation/AddressPopup';
import AnswerButton from '../../components/Assessment/AnswerButton';
import CssTransition from '../../components/Transition/CssTransition';
import ShareDrawer from '../../components/ShareDrawer';
import ToasterAlert from '../../components/Donation/ToasterAlert';
import { RockTheVoteDetails } from '../../components/Shared/rockTheVoteDetails';

import { onShowAddressPopup } from './actions';
import {
    onBackWithinCard,
    setOverlayPartnerVisible,
    setExitingCardProps,
    getExitingCardProps
} from '../Widget/actions';

import { CardTypes } from '../../utils/enums';

const FindPollingStationDetails = props => (
    <Container>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner
            transitionType="right-to-left"
            logo={RTVLogo}
            name={props.rockTheVoteTitle}
            description={props.rockTheVoteDescription}
        />
        <TransitionGroup>
            {props.addressValidationError && (
                <CssTransition key="alert" in={props.addressValidationError}>
                    <ToasterAlert content={props.addressValidationError} />
                </CssTransition>
            )}
        </TransitionGroup>
        <Body>
            <WidgetHeader />
            <Content isContactAction={false} isSingleAction={true}>
                <Title
                    isLongText={isLongText(props.title, 60)}
                    isExtraLongText={isLongText(props.title, 95)}
                    onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                    dangerouslySetInnerHTML={{
                        __html: props.title
                    }}
                />
                <DetailsContainer>
                    <Details>
                        <Description
                            onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                            isLongText={isLongText(props.subTitle, 90)}
                            isExtraLongText={isLongText(props.subTitle, 160)}
                            dangerouslySetInnerHTML={{
                                __html: props.subTitle
                            }}
                        />
                        <RockTheVoteLabel
                            color={props.theme.colors.primary}
                            onClick={e => props.setOverlayPartnerVisible(true, e, true)}
                        >
                            {props.rockTheVoteLabel}
                        </RockTheVoteLabel>
                    </Details>
                </DetailsContainer>
            </Content>
            <AutoScalingButtonSet mobileStyled={true} mobileLeftPosition={8}>
                <AnswerButton
                    text={props.registerButtonText}
                    useLoadingSpinner={false}
                    onClick={() => {
                        props.setExitingCardProps(
                            'findPollingStation',
                            props,
                            props.activeAction
                        );
                        props.onShowAddressPopup(true, props.actionIdentifier);
                    }}
                />
            </AutoScalingButtonSet>
            <Footer>
                <ShareDrawer
                    beforeBack={() => {
                        props.setExitingCardProps(
                            'findPollingStation',
                            props,
                            props.activeAction
                        );
                    }}
                    goBack={() => {
                        return props.card.type === CardTypes.actionChoice
                            ? props.onBackWithinCard(
                                  props.actionIdentifier,
                                  props.card,
                                  true
                              )
                            : null;
                    }}
                    fullwidth={props.card.type === CardTypes.actionChoice}
                    rightAlign={props.card.type !== CardTypes.actionChoice}
                    hasBackButton={props.card.type === CardTypes.actionChoice}
                    backgroundColor={'#fff'}
                />
            </Footer>
            <TransitionGroup>
                {(props.showAddressPopup || props.skipFrontCard) && (
                    <CssTransition key="alert" in={props.showAddressPopup}>
                        <AddressPopup
                            onBack={() => {
                                props.onShowAddressPopup(false, props.actionIdentifier);
                            }}
                        />
                    </CssTransition>
                )}
            </TransitionGroup>
            <TransitionGroup>
                {props.findPollingStationError && (
                    <CssTransition key="alert" in={props.findPollingStationError}>
                        <ToasterAlert content={props.findPollingStationError} />
                    </CssTransition>
                )}
            </TransitionGroup>
        </Body>
    </Container>
);

FindPollingStationDetails.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    activeAction: PropTypes.object.isRequired,
    addressValidationError: PropTypes.any,
    card: PropTypes.object.isRequired,
    findPollingStationError: PropTypes.string,
    onBackWithinCard: PropTypes.func.isRequired,
    onShowAddressPopup: PropTypes.func.isRequired,
    registerButtonText: PropTypes.string.isRequired,
    rockTheVoteDescription: PropTypes.string,
    rockTheVoteLabel: PropTypes.string.isRequired,
    rockTheVoteTitle: PropTypes.string,
    setExitingCardProps: PropTypes.func.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    showAddressPopup: PropTypes.bool,
    skipFrontCard: PropTypes.bool,
    subTitle: PropTypes.string,
    title: PropTypes.string,
    theme: PropTypes.object
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'poll',
            ['/findPollingStation', '/findPollingStation/start'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const defaultTitle =
            'Voting is one of the most powerful tools we have. Make sure you know when and where to vote on Election Day to make your voice heard.';
        const defaultSubtitle =
            'Click below to find your polling station so you can be ready.';
        const defaultButton = 'Find';

        return {
            actionIdentifier: state.widget.activeAction.identifier,
            activeAction: state.widget.activeAction,
            addressValidationError: state.widget.addressValidationError,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            findPollingStationError: state.findPollingStation.findPollingStationError,
            registerButtonText:
                state.widget.activeAction.customButtonText || defaultButton,
            rockTheVoteDescription: RockTheVoteDetails.description,
            rockTheVoteLabel: RockTheVoteDetails.label,
            rockTheVoteTitle: RockTheVoteDetails.title,
            showAddressPopup: state.findPollingStation.showAddressPopup,
            skipFrontCard: state.widget.activeAction.skipFrontCard,
            subTitle: state.widget.activeAction.text || defaultSubtitle,
            title: state.widget.activeAction.title || defaultTitle,
            theme: state.widget.theme
        };
    },
    {
        onBackWithinCard,
        onShowAddressPopup,
        setExitingCardProps,
        setOverlayPartnerVisible
    }
)(FindPollingStationDetails);
